import React from "react";
import { useFormContext } from "react-hook-form";
import { Controller } from "react-hook-form";
import ErrorMessage from "../error-message";
import { SelectFieldOption } from "../select-field/SelectInput";
import ComboBoxInput from "./ComboBoxInput";
import useFieldDisabler from "utils/custom-hooks/useFieldDisabler";

interface Props {
  label: string;
  name: any;
  defaultValue?: any;
  placeholder?: string;
  options: SelectFieldOption[];
  freeSolo?: boolean;
  disableClearable?: boolean;
  disabled?: boolean;
  errorClass?: string;
  onBlur?: (_: React.ChangeEvent<{}>) => void;
  onChange?: (_: React.ChangeEvent<{}>, value: SelectFieldOption) => void;
  onOpen?: (_: React.ChangeEvent<{}>) => void;
  roundedCorner?: boolean;
  size?: "small" | "medium";
  noBorder?: boolean; //.MuiOutlinedInput-notchedOutline
  controlTooltipOnMouseEvents?: boolean;
  paperStyle?: any;
  toolTipTitle?: string;
  showTooltip?: boolean;
  showOutsideError?: boolean;
  displaySuffix?: string;
  compareByValue?: boolean;
}

// Notes: the ComboBoxInputField form field must be an object with {id:,value:} OR {code:,value:}
const getValue = (value: any) =>
  typeof value === "object" ? value?.id || value?.code : value;

const ComboBoxInputField = (props: Props) => {
  const {
    errorClass,
    name,
    defaultValue,
    onBlur,
    onChange,
    onOpen,
    roundedCorner,
    disabled,
    noBorder = false,
    controlTooltipOnMouseEvents = false,
    showOutsideError = true,
    paperStyle,
    toolTipTitle,
    showTooltip,
    displaySuffix,
    options,
    compareByValue = false,
    ...rest
  } = props;
  const { control, setValue, getValues } = useFormContext();

  const handleOnChange = (e: any, selectedOption: SelectFieldOption) => {
    const currentValue = getValues(name);
    const newValue = selectedOption.value;
    if (currentValue?.name !== newValue) {
      setValue(name, newValue);
    }

    onChange && onChange(e, selectedOption);
  };

  const handleOnBlur = (e: any) => {
    const currentValue = getValues(name);
    const newValue = e.target.value;

    if (compareByValue) {
      const isStillValidSelection = options?.some(
        (option) => option.value === getValue(currentValue)
      );
      if (!isStillValidSelection) {
        setValue(name, newValue);
      }
    } else if (currentValue?.name !== newValue) {
      setValue(name, newValue);
    }
    onBlur && onBlur(e);
  };

  const handleOnOpen = (e: any) => {
    onOpen && onOpen(e);
  }
  
  const shouldFieldBeDisabled = useFieldDisabler(name);

  return (
    <>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field: { name, value }, fieldState: { error } }) => {
          return (
            <ComboBoxInput
              name={name}
              value={getValue(value)}
              onChange={handleOnChange}
              onBlur={handleOnBlur}
              onOpen={handleOnOpen}
              disabled={disabled || shouldFieldBeDisabled}
              roundedCorner={roundedCorner}
              hasError={Boolean(error)}
              size={props.size}
              noBorder={noBorder}
              controlTooltipOnMouseEvents={controlTooltipOnMouseEvents}
              toolTipTitle={toolTipTitle}
              showTooltip={showTooltip}
              {...rest}
              options={options}
              paperStyle={paperStyle}
              showOutsideError={showOutsideError}
              displaySuffix={displaySuffix}
            />
          );
        }}
      />
      { showOutsideError && <ErrorMessage name={name} />}
    </>
  );
};

export default ComboBoxInputField;
